import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { calculateBrsScores } from '../../utils/assessmentScoring';
import BrsResults from './BrsResults';

interface BrsAssessmentProps {
  onBack: () => void;
}

const questions = [
  { id: 1, text: 'I tend to bounce back quickly after hard times' },
  { id: 2, text: 'I have a hard time making it through stressful events' },
  { id: 3, text: 'It does not take me long to recover from a stressful event' },
  { id: 4, text: 'It is hard for me to snap back when something bad happens' },
  { id: 5, text: 'I usually come through difficult times with little trouble' },
  { id: 6, text: 'I tend to take a long time to get over setbacks in my life' }
];

const options = [
  { value: 0, label: 'Not at all' },
  { value: 1, label: 'Rarely' },
  { value: 2, label: 'Sometimes' },
  { value: 3, label: 'Often' },
  { value: 4, label: 'Always' }
];

const BrsAssessment: React.FC<BrsAssessmentProps> = ({ onBack }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [showInstructions, setShowInstructions] = useState(true);

  if (showInstructions) {
    return (
      <div className="flex flex-col h-full">
        <div className="bg-white rounded-xl shadow-lg p-6 flex-1 overflow-y-auto">
          <div className="flex items-center mb-6">
            <button
              onClick={onBack}
              className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Resilience Scale (BRS)</h2>
          </div>
          
          <div className="space-y-4 mb-8">
            <p className="text-gray-600">
              Please indicate how much you agree with each statement. There are no right or wrong answers. Please respond based on your true feelings.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Rating Scale:</h3>
              <ul className="space-y-2">
                {options.map(option => (
                  <li key={option.value}>{option.value} = {option.label}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="p-4 bg-white border-t">
          <button
            onClick={() => setShowInstructions(false)}
            className="w-full py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
          >
            Start Assessment
          </button>
        </div>
      </div>
    );
  }

  if (currentQuestion >= questions.length) {
    const scores = calculateBrsScores(answers);
    return <BrsResults scores={scores} onBack={onBack} />;
  }

  const handleAnswer = (score: number) => {
    setAnswers(prev => ({
      ...prev,
      [questions[currentQuestion].id]: score
    }));

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setCurrentQuestion(questions.length);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Question {currentQuestion + 1} / {questions.length}</h2>
              <span className="text-sm text-gray-500">
                Progress: {Math.round((currentQuestion / questions.length) * 100)}%
              </span>
            </div>
            
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-purple-500 transition-all duration-300"
                style={{ width: `${(currentQuestion / questions.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="mb-6">
            <p className="text-lg text-gray-800 mb-6">{questions[currentQuestion].text}</p>
            
            <div className="grid grid-cols-1 gap-3">
              {options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(option.value)}
                  className={`
                    p-4 rounded-lg border-2 transition-all flex items-center justify-between
                    ${answers[questions[currentQuestion].id] === option.value
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-200 hover:bg-gray-50'
                    }
                  `}
                >
                  <span className="text-gray-800">{option.label}</span>
                  <span className="font-semibold text-purple-600">{option.value}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-white border-t mt-auto">
        <div className="flex justify-between items-center">
          <button
            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
            disabled={currentQuestion === 0}
            className={`
              px-4 py-2 rounded-lg
              ${currentQuestion === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100'
              }
            `}
          >
            Previous
          </button>
          
          <div className="text-sm text-gray-500">
            {currentQuestion + 1} / {questions.length}
          </div>

          {currentQuestion === questions.length - 1 && Object.keys(answers).length === questions.length && (
            <button
              onClick={() => setCurrentQuestion(questions.length)}
              className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
            >
              View Results
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrsAssessment;